import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { RefreshCw, Download, CheckCircle2, AlertTriangle, Building2 } from "lucide-react";

const MATCH_CLS = { new: "text-emerald-700 border-emerald-300", existing: "text-sky-700 border-sky-300", conflict: "text-rose-700 border-rose-300" };

export default function AutoCountImportTab() {
  const [status, setStatus] = useState(null);
  const [depts, setDepts] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    api.get("/hr/autocount/status").then(r => setStatus(r.data)).catch(e => setStatus({ error: formatApiError(e.response?.data?.detail) }));
    api.get("/hr/departments").then(r => setDepts(r.data)).catch(() => {});
  }, []);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/hr/autocount/preview");
      const enriched = data.rows.map(r => ({ ...r, _action: r.match_status === "new" ? "new" : "skip", _dept: null, _packing: false }));
      setRows(enriched); setSummary(data.summary);
      setSel(new Set(enriched.filter(r => r.match_status === "new").map(r => r.autocount_code)));
      toast.success(`Loaded ${data.summary.records} AutoCount employees`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  const toggle = (code) => setSel(p => { const n = new Set(p); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const setRow = (code, k, v) => setRows(rs => rs.map(r => r.autocount_code === code ? { ...r, [k]: v } : r));

  const doImport = async () => {
    const chosen = rows.filter(r => sel.has(r.autocount_code));
    if (!chosen.length) return toast.error("Select at least one employee");
    if (!window.confirm(`Import ${chosen.length} employee(s) into the Employee Master?`)) return;
    setImporting(true);
    const payload = {
      rows: chosen.map(r => ({
        autocount_code: r.autocount_code, autocount_guid: r.autocount_guid, action: r._action,
        matched_employee_id: r.matched_employee_id, full_name: r.full_name, department_id: r._dept,
        branch: r.branch || "", employment_status: r.employment_status, employment_type: r.employment_type || "",
        join_date: r.join_date, confirm_date: r.confirm_date, resign_date: r.resign_date,
        email: r.email || "", phone: r.phone || "", mobile: r.mobile || "",
        is_packing: !!r._packing, sensitive: r.sensitive,
      })),
    };
    try {
      const { data } = await api.post("/hr/autocount/import", payload);
      toast.success(`Imported: ${data.created_count} new, ${data.updated_count} updated`);
      await loadPreview();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setImporting(false);
  };

  return (
    <div data-testid="hr-autocount-tab">
      {/* Status */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 mb-4 flex items-center justify-between" data-testid="autocount-status">
        {status?.error ? <span className="text-rose-600 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{status.error}</span>
          : status ? (
            <div className="flex items-center gap-6 text-sm">
              <span className="flex items-center gap-2">{status.connected ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-rose-600" />}
                <b>{status.connected ? "Connected" : "Not connected"}</b></span>
              {status.company && <span className="flex items-center gap-1.5 text-slate-600"><Building2 className="w-4 h-4" />{status.company.name} ({status.company.reg})</span>}
              {status.employees_total != null && <span className="text-slate-500">{status.employees_total} employees in AutoCount</span>}
            </div>
          ) : <span className="text-slate-400 text-sm">Checking AutoCount connection…</span>}
        <Button onClick={loadPreview} disabled={loading || !status?.connected} data-testid="autocount-load-preview" className="bg-sky-600 hover:bg-sky-700">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />Load Preview
        </Button>
      </div>

      {summary && (
        <div className="flex gap-3 mb-3 text-sm" data-testid="autocount-summary">
          <Badge variant="outline">Records: {summary.records}</Badge>
          <Badge variant="outline" className="text-emerald-700 border-emerald-300">New: {summary.new}</Badge>
          <Badge variant="outline" className="text-sky-700 border-sky-300">Existing: {summary.existing}</Badge>
          <Badge variant="outline" className="text-rose-700 border-rose-300">Conflict: {summary.conflict}</Badge>
          <span className="ml-auto text-slate-500">{sel.size} selected</span>
          <Button size="sm" onClick={doImport} disabled={importing} data-testid="autocount-import-button" className="bg-slate-900">
            <Download className="w-4 h-4 mr-1.5" />Import Selected
          </Button>
        </div>
      )}

      {rows.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="excel-table w-full" data-testid="autocount-rows-table">
            <thead><tr><th></th><th>Match</th><th>AutoCount Code</th><th>Name</th><th>Dept (AC)</th><th>Status</th><th>Action</th><th>Map Department</th><th>Packing?</th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.autocount_code} data-testid={`autocount-row-${r.autocount_code}`}>
                  <td><Checkbox checked={sel.has(r.autocount_code)} onCheckedChange={() => toggle(r.autocount_code)} data-testid={`autocount-check-${r.autocount_code}`} /></td>
                  <td><Badge variant="outline" className={MATCH_CLS[r.match_status]}>{r.match_status}{r.matched_fas_id ? ` → ${r.matched_fas_id}` : ""}</Badge></td>
                  <td className="font-mono text-xs">{r.autocount_code}</td>
                  <td>{r.full_name}</td>
                  <td className="text-xs text-slate-500">{r.department_name || r.department_code || "—"}</td>
                  <td>{r.employment_status}</td>
                  <td>
                    <Select value={r._action} onValueChange={v => setRow(r.autocount_code, "_action", v)}>
                      <SelectTrigger className="h-8 w-24" data-testid={`autocount-action-${r.autocount_code}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        {r.matched_employee_id && <SelectItem value="update">Update</SelectItem>}
                        <SelectItem value="skip">Skip</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td>
                    <Select value={r._dept || "none"} onValueChange={v => setRow(r.autocount_code, "_dept", v === "none" ? null : v)}>
                      <SelectTrigger className="h-8 w-40"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">—</SelectItem>{depts.map(d => <SelectItem key={d.id} value={d.id}>{d.kind === "team" ? "— " : ""}{d.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td><Checkbox checked={r._packing} onCheckedChange={v => setRow(r.autocount_code, "_packing", v)} data-testid={`autocount-packing-${r.autocount_code}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
